import React, { useState } from 'react';
import PropTypes from 'prop-types';
import CommentThread from './CommentThread.jsx';

/**
 * Sidebar component for displaying comments
 */
const CommentSidebar = ({
  comments = [],
  stats = {},
  onReply,
  onEdit,
  onDelete,
  currentUserId,
  isVisible = false,
  onClose,
  highlightedComments = [], // Array of comment IDs that should be highlighted
  className = '',
  isDarkMode = false
}) => {
  const [activeComment, setActiveComment] = useState(null);

  /**
   * Handle comment click
   */
  const handleCommentClick = (comment) => {
    setActiveComment(activeComment?.id === comment.id ? null : comment);
  };

  return (
    <div
      className={`comment-sidebar ${className} ${isVisible ? 'visible' : ''}`}
      style={{
        position: 'fixed',
        top: '0',
        right: isVisible ? '0' : '-400px',
        width: '400px',
        height: '100vh',
        backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
        borderLeft: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
        boxShadow: isVisible ? '-4px 0 6px -1px rgba(0, 0, 0, 0.1)' : 'none',
        zIndex: 1000,
        transition: 'right 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px',
          borderBottom: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
          backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '8px'
          }}
        >
          <h3
            style={{
              fontSize: '18px',
              fontWeight: '600',
              color: isDarkMode ? '#f9fafb' : '#374151',
              margin: 0
            }}
          >
            Comments
          </h3>
          <button
            onClick={onClose}
            style={{
              padding: '4px',
              border: 'none',
              backgroundColor: 'transparent',
              color: isDarkMode ? '#9ca3af' : '#6b7280',
              cursor: 'pointer',
              borderRadius: '4px',
              fontSize: '18px',
              lineHeight: 1,
              transition: 'all 0.2s ease'
            }}
            title="Close comments"
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = isDarkMode ? '#374151' : '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            ×
          </button>
        </div>
        
        {/* Stats */}
        <div
          style={{
            fontSize: '14px',
            color: isDarkMode ? '#9ca3af' : '#6b7280'
          }}
        >
          {stats.totalComments > 0 ? (
            <span>
              {stats.totalComments} {stats.totalComments === 1 ? 'comment' : 'comments'} 
              {stats.replies > 0 && ` (${stats.replies} replies)`}
            </span>
          ) : (
            <span>No comments yet</span>
          )}
        </div>
      </div>

      {/* Comments list */}
      <div
        style={{
          flex: 1,
          padding: '16px',
          overflowY: 'auto'
        }}
      >
        {comments.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '32px 16px',
              color: isDarkMode ? '#9ca3af' : '#6b7280'
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
              No comments yet
            </div>
            <div style={{ fontSize: '14px' }}>
              Select text to add the first comment
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {comments.map((comment) => (
              <CommentThread
                key={comment.id}
                comment={comment}
                onReply={onReply}
                onEdit={onEdit}
                onDelete={onDelete}
                currentUserId={currentUserId}
                className="sidebar-comment"
                isHighlighted={highlightedComments.includes(comment.id)}
                isDarkMode={isDarkMode}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '16px',
          borderTop: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
          backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
          position: 'sticky',
          bottom: 0
        }}
      >
        <div
          style={{
            fontSize: '12px',
            color: isDarkMode ? '#9ca3af' : '#6b7280',
            textAlign: 'center'
          }}
        >
          Select text to add comments
        </div>
      </div>
    </div>
  );
};

CommentSidebar.propTypes = {
  comments: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    user: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      email: PropTypes.string,
      profile_image: PropTypes.string
    }).isRequired,
    replies: PropTypes.array
  })),
  stats: PropTypes.shape({
    totalComments: PropTypes.number,
    topLevelComments: PropTypes.number,
    replies: PropTypes.number
  }),
  onReply: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  currentUserId: PropTypes.string,
  isVisible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  highlightedComments: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  isDarkMode: PropTypes.bool
};

export default CommentSidebar;
